/* eslint-disable fp/no-mutation */
import type { TaskChatAuthorInfo } from '@frontend/api-client';
import type { SavedFile, TaskComment } from '@frontend/api-types';
import { dateFormatters, match } from '@frontend/duck-tape';
import { makeSavedFileList } from '@frontend/forms';
import { useFeatureIsOn, useState, useVisibilityController } from '@frontend/react';
import {
  BlinkingDot,
  ConfirmationModal,
  FilePreviewModal,
  Icon,
  RenderHTML,
  RenderMarkdown,
  Text,
  XStack,
  YStack,
} from '@frontend/web-react';
import { applyClass } from '@frontend/web-utils';
import { GameplanCard } from '@frontend/web/components/DomainSpecific/Tasks/Chat/GameplanCard';
import { TaskChatAuthor } from '@frontend/web/components/DomainSpecific/Tasks/Chat/TaskChatMessageBubble/TaskChatAuthor';
import { TaskChatMessageFooter } from '@frontend/web/components/DomainSpecific/Tasks/Chat/TaskChatMessageBubble/TaskChatMessageFooter';
import { memo } from 'react';
import { ChatFilePreview } from '../ChatFilePreview';

type TaskChatMessageBubbleProps = Pick<TaskComment, 'attachments' | 'body' | 'complexityAgreement'> & {
  authorInfo: TaskChatAuthorInfo;
  contentType?: 'html' | 'markdown' | 'plain';
  createdAt?: DateTimeISOString;
  isAuthorVisible: boolean;
  isStreaming?: boolean;
  onAgreementActionComplete?: () => Promise<void> | void;
  taskId?: string;
};

const TaskChatMessageBubbleComponent = ({
  attachments,
  authorInfo,
  body,
  complexityAgreement,
  contentType = 'html',
  createdAt,
  isAuthorVisible,
  isStreaming,
  onAgreementActionComplete,
  taskId,
}: TaskChatMessageBubbleProps) => {
  const { enabled: isGameplanPricingEnabled } = useFeatureIsOn('is_gameplan_pricing_enabled');
  const [isFilePreviewModalOpen, { close: closeFilePreviewModal, open: openFilePreviewModal }] =
    useVisibilityController();
  const savedAttachments = makeSavedFileList(attachments, (a) => ({ ...a, key: a.fileName, url: a.contentUrl }));
  const [selectedAttachment, setSelectedAttachment] = useState<SavedFile>();

  const isSuperAgentMessage = authorInfo.type === 'super_agent';
  const isClientMessage = authorInfo.type === 'client' || isSuperAgentMessage;
  const [isSentViaTextModalOpen, { close: closeSentViaTextModal, open: openSentViaTextModal }] =
    useVisibilityController();

  // Render message content based on content type
  const renderContent = () => (
    <>
      <div data-task-chat-search-content="true">
        {match(contentType)
          .with('html', () => <RenderHTML className="text-neutral-950" html={body} />)
          .with('markdown', () => (
            <RenderMarkdown className="duckbill-markdown prose max-w-full">{body}</RenderMarkdown>
          ))
          .with('plain', () => <Text className="text-neutral-950">{body}</Text>)
          .exhaustive()}
      </div>
      {!isClientMessage && !complexityAgreement ? <TaskChatMessageFooter copyContent={body} /> : null}
      {isStreaming ? <BlinkingDot /> : null}
      {complexityAgreement && taskId ? (
        <GameplanCard
          agreement={complexityAgreement}
          onAcceptedOrDeclined={onAgreementActionComplete}
          taskId={taskId}
        />
      ) : null}
    </>
  );

  // Render attachments
  const renderAttachments = () =>
    attachments.length ? (
      <YStack gapY="md">
        {savedAttachments.map((attachment) => (
          <ChatFilePreview
            file={attachment}
            key={attachment.id}
            onClick={(file) => {
              setSelectedAttachment(file);
              openFilePreviewModal();
            }}
          />
        ))}
      </YStack>
    ) : null;

  if (isGameplanPricingEnabled) {
    return (
      <YStack className={applyClass(isClientMessage, 'items-end', 'items-start')}>
        {isClientMessage ? (
          <>
            {/* eslint-disable-next-line tailwindcss/no-arbitrary-value */}
            <YStack bg="cream100" className="max-w-[70%]" px="md" py="sm" radius="lg">
              {renderContent()}
              {renderAttachments()}
            </YStack>
            {isSuperAgentMessage ? (
              <>
                <XStack className="cursor-pointer items-center gap-x-sm pt-xs" onClick={openSentViaTextModal}>
                  <Icon className="-scale-x-100" color="tertiary" name="IconCornerDownLeft" size="sm" />
                  <Text color="tertiary" type="p-sm">
                    sent on your behalf
                  </Text>
                </XStack>
                <ConfirmationModal
                  hideBodyTitle
                  isOpen={isSentViaTextModalOpen}
                  onClickSubmit={closeSentViaTextModal}
                  onClose={closeSentViaTextModal}
                  showHeaderTitle
                  submitLabel="Got it"
                  subtitle={
                    <YStack gapY="md">
                      <Text type="p-md">
                        You texted us and we turned it into a message for your Duckbill assistant.
                      </Text>
                      <Text type="p-md">
                        When you text us, {"we'll"} relay your requests to your assistant here so things keep moving.
                        You can always send messages directly from this chat too.
                      </Text>
                    </YStack>
                  }
                  title="Sent without lifting a finger."
                />
              </>
            ) : null}
          </>
        ) : (
          <YStack className="w-full group" gap="xs">
            {createdAt ? (
              <Text color="tertiary" type="p-sm">
                {dateFormatters.message(createdAt)}
              </Text>
            ) : null}
            {renderContent()}
            {renderAttachments()}
          </YStack>
        )}
        <FilePreviewModal
          file={selectedAttachment}
          isOpen={isFilePreviewModalOpen}
          onClose={closeFilePreviewModal}
          title={selectedAttachment?.key}
        />
      </YStack>
    );
  }
  return (
    <YStack gapY="md">
      {isAuthorVisible ? <TaskChatAuthor {...authorInfo} /> : null}
      <YStack className="pl-xl" gapY="md">
        <YStack>
          <div data-task-chat-search-content="true">
            {match(contentType)
              .with('html', () => <RenderHTML html={body} />)
              .with('markdown', () => (
                <RenderMarkdown className="duckbill-markdown prose max-w-full">{body}</RenderMarkdown>
              ))
              .with('plain', () => <Text>{body}</Text>)
              .exhaustive()}
          </div>
          {isStreaming ? <BlinkingDot /> : null}
        </YStack>
        {attachments.length ? (
          <YStack gapY="md">
            {savedAttachments.map((attachment) => (
              <ChatFilePreview
                file={attachment}
                key={attachment.id}
                onClick={(file) => {
                  setSelectedAttachment(file);
                  openFilePreviewModal();
                }}
              />
            ))}
          </YStack>
        ) : null}
        <FilePreviewModal
          file={selectedAttachment}
          isOpen={isFilePreviewModalOpen}
          onClose={closeFilePreviewModal}
          title={selectedAttachment?.key}
        />
      </YStack>
    </YStack>
  );
};

export const TaskChatMessageBubble = memo(TaskChatMessageBubbleComponent);
